import React, { useState, useEffect, useRef } from 'react';
import {
    Alert,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Grid,
    Typography,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Collapse,
    IconButton,
    FormControl,
    FormLabel,
    Radio,
    RadioGroup,
    DialogTitle
} from '@mui/material';
import { ExpandLess, ExpandMore, Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { IReduxState } from 'utils/models/redux-states';
import CancelConfirmationDialog from '../dialog/Cancel';
import PrimaryButton from '../button/PrimaryButton';
import DropdownField from '../drop-down/DropdownField';
import ApiService from 'utils/services/ApiService';
import { CocPreset, CocGroup, CocPanel, CocAnalysis } from 'utils/models/coc';
import { ThemeProvider } from '@mui/material/styles';
import theme from 'theme/muiTheme';
import SampleTable, { SampleRow } from './SampleTable';
import REGEX_CONSTANTS from 'utils/constants/regex';
import PackingSlip from './PackingSlip';
import { CocAnalysisVO, CocForm, CocSite } from './LabRequests';
import countries from 'utils/constants/Countries';
import CONSTANTS from 'utils/constants/UIConstants';
import LabRequestDetails from './LabRequestDetails';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import SelectableCardGroup from "../radio/SelectableCardsGroup";
import AnalysisSelect from '../Dropdowns/AnalysisSelect';
import { usePopupMessage } from '../../hooks/SendPopUpMessage';
import ShrinkLabelTextField from '../text-field/ShrinkLabelTextField';

interface LabRequestFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmitSuccess?: (submittedCocForm: any, isFoodLab: boolean) => void;
    isChampionX?: boolean;
    cocSites: CocSite[];
    latestForm: CocForm;
    title?: string;
    maxWidthOverride?: number;
    foodLabEnabled?: boolean;
}

interface FormData {
    labId: number | null;
    contactName: string;
    company: string;
    address: {
        street1: string;
        street2: string;
        city: string;
        postalCode: string;
        provinceName: string;
        country: string;
    };
    officePhone: string;
    contactEmail: string;
    purchaseOrder: string;
    quoteNumber: string;
    costCenter: string;
    isRushOrder: boolean;
    specialHandling: string;
    holdTimeInstruction: string;
    collaborators: string[];
    collaboratorErrors: { [key: number]: string };
}

interface CocFormData {
    selectedPresetId: number | null;
    selectedGroups: number[];
    selectedAnalyses: number[];
    selectedPanels: Record<number, {
        isSelected: boolean;
        isIndeterminate: boolean;
        selectedAnalyses: number[];
    }>;
}


const LabRequestForm: React.FC<LabRequestFormProps> = ({
    isOpen,
    onClose,
    onSubmitSuccess,
    isChampionX = false,
    cocSites,
    latestForm,
    title,
    maxWidthOverride,
    foodLabEnabled,
}) => {
    const userData = useSelector((state: IReduxState) => state.user?.userInformation);
    const [formData, setFormData] = useState<FormData>({
        labId: foodLabEnabled ? (latestForm.labId ?? 1) : 1,
        contactName: '',
        company: '',
        address: {
            street1: '',
            street2: '',
            city: '',
            postalCode: '',
            provinceName: '',
            country: '',
        },
        officePhone: '',
        contactEmail: '',
        purchaseOrder: '',
        quoteNumber: '',
        costCenter: '',
        isRushOrder: false,
        specialHandling: '',
        holdTimeInstruction: '',
        collaborators: [''], // Initialize with one empty collaborator field
        collaboratorErrors: {},
    });

    const isFoodLab = !!foodLabEnabled && formData.labId === 2;
    const sendPopUpMessage = usePopupMessage();
    const [sameAnalysesForAll, setSameAnalysesForAll] = useState(false);
    const [sharedAnalysesError, setSharedAnalysesError] = useState(false);
    const [sharedOtherTextError, setSharedOtherTextError] = useState(false);
    const [isSwitchLabDialogOpen, setIsSwitchLabDialogOpen] = useState(false);
    const [pendingLabId, setPendingLabId] = useState<number | null>(null);
    const pristineSnapshotRef = useRef<string | null>(null);
    // CoC Lab Services state
    const [cocData, setCocData] = useState<CocFormData>({
        selectedPresetId: null,
        selectedGroups: [],
        selectedAnalyses: [],
        selectedPanels: {},
    });
    const [presets, setPresets] = useState<CocPreset[]>([]);
    const [groups, setGroups] = useState<CocGroup[]>([]);
    const [panels, setPanels] = useState<Record<number, CocPanel[]>>({});
    const [foodAnalyses, setFoodAnalyses] = useState<CocAnalysis[]>([]);
    const [selectedAnalyses, setSelectedAnalyses] = useState<CocAnalysisVO[]>([]);
    const [loading, setLoading] = useState(false);
    const [isPackingSlipOpen, setIsPackingSlipOpen] = useState(false);
    const [isCancelFormDialogOpen, setIsCancelFormDialogOpen] = useState(false);
    const [cocId, setCocId] = useState("");
    const [submissionDate, setSubmissionDate] = useState("");
    const [contactName, setContactName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [micCAddon, setMicCAddon] = useState(false);
    const [paymentInformation, setPaymentInformation] = useState("CC");

    const [nameError, setNameError] = useState("");
    const [companyError, setCompanyError] = useState("");
    const [streetError, setStreetError] = useState("");
    const [cityError, setCityError] = useState("");
    const [provinceError, setProvinceError] = useState("");
    const [postalError, setPostalError] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [purchaseOrderError, setPurchaseOrderError] = useState("");
    const [analysesError, setAnalysesError] = useState("");
    const [samplesError, setSamplesError] = useState(false);
    const [collectionDateError, setCollectionDateError] = useState(false);
    const [addMoreError, setAddMoreError] = useState(false);
    const [validCollaborators, setValidCollaborators] = useState<string[]>([]);
    const [submissionData, setSubmissionData] = useState<any>(null);

    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [isReviewMode, setIsReviewMode] = useState(true);
    const sampleSectionRef = useRef<HTMLDivElement>(null);
    const analysesSectionRef = useRef<HTMLDivElement>(null);
    const [cocForm, setCocForm] = useState<CocForm>({
        labId: 1,
        submissionDate: "",
        contactName: "",
        company: "",
        email: "",
        purchaseOrderNumber: "",
        quoteNumber: "",
        rushOrder: "",
        specialRequests: "",
        unpreservedInstructions: "",
        cocId: "",
        collaboratorEmails: ([]),
        cocSamples: ([]),
        cocAnalysisVOs: ([]),
        sameAnalysesForAll: false,
        addressInfo: {
            streetAddress1: "",
            streetAddress2: "",
            city: "",
            officePhone: "",
            countryName: "",
            postalCode: "",
            provinceName: "",
            id: 0
        }
    });

    const getDefaultCustomerInfo = () => ({
        contactName: userData ? `${userData.firstName} ${userData.lastName}` : '',
        contactEmail: userData?.email ?? '',
        officePhone: latestForm.addressInfo.officePhone ?? '',
        company: getCompany(),
        purchaseOrder: '',
        quoteNumber: '',
        costCenter: latestForm.costCenterNumber ?? '',
        holdTimeInstruction: '',
        isRushOrder: false,
        specialHandling: '',
        collaborators: [''],
        address: {
            country: latestForm.addressInfo.countryName === '' ? userData?.addressVO?.countryName ?? '' : latestForm.addressInfo.countryName ?? '',
            city: latestForm.addressInfo.city ?? '',
            postalCode: latestForm.addressInfo.postalCode ?? '',
            provinceName: latestForm.addressInfo.provinceName ?? '',
            street1: latestForm.addressInfo.streetAddress1 ?? '',
            street2: latestForm.addressInfo.streetAddress2 ?? '',
        },
    });

    function clearForm() {
        setCocData(prev => ({
            ...prev,
            company: '',
            selectedAnalyses: [],
            selectedPanels: {},
        }));
        setMicCAddon(false);
        setSameAnalysesForAll(false);
        setSharedAnalysesError(false);
        setSharedOtherTextError(false);
        setPaymentInformation('CC');
        setNameError('');
        setCompanyError('');
        setStreetError('');
        setCityError('');
        setProvinceError('');
        setPostalError('');
        setPhoneError('');
        setPurchaseOrderError('');
        setAnalysesError('');
        setSamplesError(false);
        setCollectionDateError(false);
        setAddMoreError(false);

        setFormData(prev => ({
            ...prev,
            ...getDefaultCustomerInfo(),
            labId: prev.labId,
            collaboratorErrors: {},
        }));

        setSampleRows([
            {
                sampleNumber: 1,
                sampleName: '',
                sampleSite: '',
                sampleLocation: '',
                sampleType: '',
                amountCollected: '',
                units: '',
                preserved: true,
                collectionDate: fillNewDate(),
                siteOptions: [],
                locationOptions: [],
                siteSource: '',
                locationSource: '',
                siteId: '',
                locationId: '',
                analyses: [],
                customAnalysis: '',
                clientSampleId: '',
                foodSampleType: '',
                shelfLife: '',
                storageConditions: '',
                comments: '',
            },
        ]);
        setSelectedAnalyses([]);
        setValidCollaborators([]);
        setSubmissionData(null);
        pristineSnapshotRef.current = null;
    }

    function getCompany() : string {
        if (isChampionX) {
            return '';
        } else if (latestForm.company === '' && userData) {
            return userData.parentCompany;
        } else {
            return latestForm.company;
        }
    }

    function fillNewDate() : string {
        let date = new Date();
        let month = addLeadingZeroIfNeeded(date.getMonth() + 1 + '');
        let day = addLeadingZeroIfNeeded(date.getDate() + '');
        let hours = addLeadingZeroIfNeeded(date.getHours() + '');
        let minutes = addLeadingZeroIfNeeded(date.getMinutes() + '');

        return date.getFullYear() + '-' + month + '-' + day + 'T' + hours + ':' + minutes;
    }

    function addLeadingZeroIfNeeded(stringIn: string) {
        if (stringIn.length < 2) {
            return "0" + stringIn;
        } else {
            return stringIn;
        }
    }

    useEffect(() => {
        if (userData) {
            setFormData(prev => ({
                ...prev,
                ...getDefaultCustomerInfo(),
                labId: prev.labId,
            }));
        }
    }, [userData, isChampionX, latestForm]);

    // Load presets on component mount
    useEffect(() => {
        const loadPresets = async () => {
            try {
                setLoading(true);
                const response = await ApiService.getCocPresets(isFoodLab ? 2 : 1);
                setPresets(response.data);

                if (isFoodLab) {
                    const analysisResponses = await Promise.all(response.data.map(async (preset: CocPreset) => {
                        const groupsResponse = await ApiService.getCocGroups(preset.id);
                        return Promise.all(groupsResponse.data.map(async (group: CocGroup) => {
                            const panelsResponse = await ApiService.getCocPanelAnalyses(group.id);
                            return panelsResponse.data.flatMap((panel: CocPanel) => panel.analyses || []);
                        }));
                    }));
                    const analyses = analysisResponses.flat(2);
                    setFoodAnalyses(Array.from(new Map(analyses.map((analysis: CocAnalysis) => [analysis.id, analysis])).values()));
                } else {
                    setFoodAnalyses([]);
                }
                
                // Load last selected preset from localStorage if available
                const lastPresetId = localStorage.getItem('lastCocPresetId');
                if (lastPresetId) {
                    handlePresetChange(parseInt(lastPresetId));
                }
            } catch (error) {
                console.error('Error loading presets:', error);
            } finally {
                setLoading(false);
            }
        };

        loadPresets();
    }, [isFoodLab]);

    const handlePresetChange = async (presetId: number) => {
        try {
            setLoading(true);
            const response = await ApiService.getCocGroups(presetId);
            let sortedGroups: CocGroup[] = response.data;
            sortedGroups.sort((group1: CocGroup, group2: CocGroup) => 
                group1.id - group2.id
            );
            setGroups(sortedGroups);
            setCocData(prev => ({
                ...prev,
                selectedPresetId: presetId,
                selectedAnalyses: [],
                selectedPanels: {},
            }));
            setSelectedAnalyses([]);
            localStorage.setItem('lastCocPresetId', presetId.toString());

            for (const group of sortedGroups) {
                const isSelected = cocData.selectedGroups.includes(group.id);

                if (!isSelected) {
                    handleGroupChange(group.id);
                }
                
            }

        } catch (error) {
            console.error('Error loading groups:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGroupChange = async (groupId: number) => {
        const isSelected = cocData.selectedGroups.includes(groupId);

        setCocData(prev => ({
            ...prev,
            selectedGroups: isSelected
                ? prev.selectedGroups.filter(id => id !== groupId)
                : [...prev.selectedGroups, groupId],
        }));

        if (!isSelected && !panels[groupId]) {
            try {
                setLoading(true);
                const response = await ApiService.getCocPanelAnalyses(groupId);
                
                let sortedGroupAnalyses = splitDwarves(response.data);

                sortedGroupAnalyses.sort((panel1: any, panel2: any) => {
                    panel1.analyses.sort((analysis1: any, analysis2: any) => {
                        return analysis1.id - analysis2.id;
                    });
                    panel2.analyses.sort((analysis1: any, analysis2: any) => {
                        return analysis1.id - analysis2.id;
                    });

                    if (panel1.analyses.length < 1 || panel2.analyses.length < 1) {
                        return 0;
                    } else {
                        return panel1.analyses[0].id - panel2.analyses[0].id;
                    }
                    
                });

                setPanels(prev => ({
                    ...prev,
                    [groupId]: sortedGroupAnalyses,
                }));
            } catch (error) {
                console.error('Error loading panels for group:', groupId, error);
                setCocData(prev => ({
                    ...prev,
                    selectedGroups: prev.selectedGroups.filter(id => id !== groupId)
                }));
            } finally {
                setLoading(false);
            }
        }
    };

    function splitDwarves(panelList: any) {
        let dwarvesIndex = panelList.findIndex((panel: any) => panel.name === "No Panel");
        let dwarves = panelList[dwarvesIndex];

        for (const dwarf of dwarves.analyses) {
            panelList.push({
                name: dwarves.name,
                analyses: [{
                    name: dwarf.name,
                    variant: dwarf.variant,
                    id: dwarf.id
                }],
            });
        }

        panelList.splice(dwarvesIndex, 1);
        return panelList;
    }

    const handlePanelChange = (groupId: number, panelId: number, checked: boolean) => {
        const panel = panels[groupId]?.find(p => p.id === panelId);
        if (!panel) return;

        const analysisIds = panel.analyses.map(a => a.id);
        const analysisObjs = panel.analyses;
        setSelectedAnalyses(prev => {
            if (checked) {
                let filteredPrev = prev.filter(analysis => !analysisIds.includes(analysis.id));
                return [...filteredPrev, ...analysisObjs]
            } else {
                return prev.filter(analysis => !analysisIds.includes(analysis.id));
            }
        })
        
        setCocData(prev => {
            const newSelectedAnalyses = checked
                ? [...prev.selectedAnalyses, ...analysisIds]
                : prev.selectedAnalyses.filter(id => !analysisIds.includes(id));

            const panelState = {
                isSelected: checked,
                isIndeterminate: false,
                selectedAnalyses: checked ? analysisIds : []
            };

            return {
                ...prev,
                selectedAnalyses: newSelectedAnalyses,
                selectedPanels: {
                    ...prev.selectedPanels,
                    [panelId]: panelState
                }
            };
        });
    };
    const openCacelFormDialog = () => {
        if (!hasFormData()) {
            clearForm();
            onClose();
            return;
        }
		setIsCancelFormDialogOpen(true);
	};

	const closeCancelFormDialog = () => {
        console.log('updating open state to closed')
		setIsCancelFormDialogOpen(false);
	};
    const handleCancelForm = () => {
        clearForm();
        onClose();
        closeCancelFormDialog();
    };

    const handleAnalysisChange = (groupId: number, panelId: number, analysisId: number, checked: boolean, analysis: any) => {
        const panel = panels[groupId]?.find(p => p.id === panelId);
        if (!panel) return;

        if (checked === true) {
            setSelectedAnalyses(prev => [...prev, {id: analysisId, name: analysis.name, variant: analysis.variant}]);
        } else {
            setSelectedAnalyses(prev => prev.filter(a => a.id !== analysisId));
        }

        setCocData(prev => {
            const newSelectedAnalyses = checked
                ? [...prev.selectedAnalyses, analysisId]
                : prev.selectedAnalyses.filter(id => id !== analysisId);

            const panelAnalyses = panel.analyses.map(a => a.id);
            const selectedPanelAnalyses = newSelectedAnalyses.filter(id => panelAnalyses.includes(id));
            
            const isSelected = selectedPanelAnalyses.length === panelAnalyses.length;
            const isIndeterminate = selectedPanelAnalyses.length > 0 && !isSelected;

            return {
                ...prev,
                selectedAnalyses: newSelectedAnalyses,
                selectedPanels: {
                    ...prev.selectedPanels,
                    [panelId]: {
                        isSelected,
                        isIndeterminate,
                        selectedAnalyses: selectedPanelAnalyses
                    }
                }
            };
        });
    };

    const handleInputChange = (field: string, value: string | boolean, errorFunction: any = null) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
        checkErrorField(value, errorFunction);
    };

    const handleAddressChange = (field: string, value: string, errorFunction: any = null) => {
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                [field]: value,
            },
        }));
        checkErrorField(value, errorFunction);
    };

    function checkErrorField (value: string | boolean, errorFunction: any) {
        if (value === "" && errorFunction) {
            errorFunction("Please provide a value.");
            return true;
        } else if (value !== "" && errorFunction) {
            errorFunction("");
        }
        return false;
    }

    function checkFormErrors () {
        //Run each of these so they all highlight on the form at once
        let nameError = checkErrorField(formData.contactName, setNameError);
        let companyError = isChampionX ? false : checkErrorField(formData.company, setCompanyError);
        let streetError = checkErrorField(formData.address.street1, setStreetError);
        let cityError = checkErrorField(formData.address.city, setCityError);
        let postalError = checkErrorField(formData.address.postalCode, setPostalError);
        let provinceError = checkErrorField(formData.address.provinceName, setProvinceError);
        let phoneError = isChampionX ? false : checkErrorField(formData.officePhone, setPhoneError);
        let purchaseOrderError = paymentInformation === 'PO' ? checkErrorField(formData.purchaseOrder, setPurchaseOrderError): false;
        if(paymentInformation === 'PO' && checkErrorField(formData.purchaseOrder, setPurchaseOrderError)){
            setPurchaseOrderError("Please fill in the Purchase Order #")
            purchaseOrderError = true;
        }else{
            setPurchaseOrderError("");
            purchaseOrderError = false;
        }
        let analysesError = false;
        if (!isChampionX && !isFoodLab && cocData.selectedAnalyses.length === 0) {
            analysesError = true;
            setAnalysesError("Please select at least one analysis for your samples.");
        } else {
            setAnalysesError("");
        }

        let sampleError = false;
        let rowsChanged = false;
        const validatedSampleRows = sampleRows.map(row => {
            const errors = { ...(row.hasErrors || {}) };
            let changed = false;
            if (isFoodLab) {
                const needsClientSampleIdError = row.clientSampleId.trim() === '';
                if (needsClientSampleIdError) {
                    errors.clientSampleId = true;
                    changed = true;
                }
                if (needsClientSampleIdError ||
                    row.collectionDate === null ||
                    row.collectionDate === "" ||
                    row.collectionDate === undefined
                ) {
                    sampleError = true;
                    setSamplesError(true);
                }
            } else {
                const needsSampleTypeError = row.sampleType.trim() === '';
                if (needsSampleTypeError) {
                    errors.sampleType = true;
                    changed = true;
                }
                if (needsSampleTypeError ||
                    row.amountCollected === "" ||
                    row.collectionDate === null ||
                    row.collectionDate === "" ||
                    row.collectionDate === undefined
                ) {
                    sampleError = true;
                    setSamplesError(true);
                }
            }
            if (row.collectionDate !== null && new Date(row.collectionDate).getTime() > new Date().getTime()) {
                sampleError = true;
                setCollectionDateError(true);
            }
            if (changed) rowsChanged = true;
            return changed ? { ...row, hasErrors: errors } : row;
        });
        if (isFoodLab) {
            if (sameAnalysesForAll) {
                const sharedRow = sampleRows[0];
                if (!sharedRow || sharedRow.analyses.length === 0) {
                    sampleError = true;
                    setSamplesError(true);
                    setSharedAnalysesError(true);
                } else {
                    setSharedAnalysesError(false);
                }
                if (foodOtherAnalysisId !== undefined && sharedRow?.analyses.includes(foodOtherAnalysisId) && sharedRow.customAnalysis.trim() === '') {
                    sampleError = true;
                    setSamplesError(true);
                    setSharedOtherTextError(true);
                } else {
                    setSharedOtherTextError(false);
                }
                if (rowsChanged) {
                    setSampleRows(validatedSampleRows);
                }
            } else {
                let analysisRowsChanged = false;
                const newRows = validatedSampleRows.map(row => {
                    const errors = { ...(row.hasErrors || {}) };
                    let changed = false;

                    const needsAnalysesError = row.analyses.length === 0;
                    if (needsAnalysesError !== !!errors.analyses) {
                        if (needsAnalysesError) errors.analyses = true;
                        else delete errors.analyses;
                        changed = true;
                    }
                    if (needsAnalysesError) {
                        sampleError = true;
                        setSamplesError(true);
                    }

                    const needsOtherError = foodOtherAnalysisId !== undefined && row.analyses.includes(foodOtherAnalysisId) && row.customAnalysis.trim() === '';
                    if (needsOtherError !== !!errors.customAnalysis) {
                        if (needsOtherError) errors.customAnalysis = true;
                        else delete errors.customAnalysis;
                        changed = true;
                    }
                    if (needsOtherError) {
                        sampleError = true;
                        setSamplesError(true);
                    }

                    if (changed) { analysisRowsChanged = true; return { ...row, hasErrors: errors }; }
                    return row;
                });
                if (rowsChanged || analysisRowsChanged) {
                    setSampleRows(newRows);
                }
            }
        } else if (rowsChanged) {
            setSampleRows(validatedSampleRows);
        }

        const nonEmptyNames = sampleRows.map(r => r.sampleName).filter(n => n !== '');
        const uniqueNames = new Set(nonEmptyNames);
        let duplicateNameError = false;

        if (nonEmptyNames.length !== uniqueNames.size) {
            duplicateNameError = true;
            setSamplesError(true);
        }
        if ((sampleError || duplicateNameError) && sampleSectionRef.current) {
            sampleSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (analysesError && analysesSectionRef.current) {
            analysesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (!sampleError && !duplicateNameError) {
            setSamplesError(false);
            setAddMoreError(false);
        }
        if (!sampleError) {
            setCollectionDateError(false);
        }

        const errors = {
            nameError,
            companyError,
            streetError,
            cityError,
            postalError,
            provinceError,
            phoneError,
            purchaseOrderError,
            analysesError,
            sampleError,
            duplicateNameError,
        };

        const activeErrors = Object.entries(errors)
            .filter(([, isError]) => isError)
            .map(([key]) => key);

        addEventLog(`CoC Form Reviewed - ${activeErrors.length > 0 ? 'Errors' : 'No Errors'}`, "Lab Request", undefined, undefined, `Errors: ${activeErrors.join(', ')}`);
        return activeErrors.length > 0;
    }

    // Sample Information Table State and Types
    const sampleTypeToUnits = {
        Liquid: 'mL',
        Solid: 'g',
        Swab: 'cm²',
    };
    const foodOtherAnalysisId = foodAnalyses.find((analysis) => analysis.name.trim().toLowerCase() === 'other')?.id;
    const [sampleRows, setSampleRows] = useState<SampleRow[]>([
        {
            sampleNumber: 1,
            sampleName: '',
            sampleSite: '',
            sampleLocation: '',
            sampleType: '',
            amountCollected: '',
            units: '',
            preserved: true,
            collectionDate: fillNewDate(),
            siteOptions: [],
            locationOptions: [],
            siteSource: '',
            locationSource: '',
            siteId: '',
            locationId: '',
            analyses: [],
            customAnalysis: '',
            clientSampleId: '',
            foodSampleType: '',
            shelfLife: '',
            storageConditions: '',
            comments: '',
        },
    ]);
    const buildComparableState = () => JSON.stringify({
        formData: { ...formData, labId: undefined, collaboratorErrors: {} },
        sampleRows: sampleRows.map(({ hasErrors, ...rest }) => rest),
        cocData,
        selectedAnalyses,
        micCAddon,
        paymentInformation,
        sameAnalysesForAll,
    });

    useEffect(() => {
        if (formData.labId !== null && pristineSnapshotRef.current === null) {
            pristineSnapshotRef.current = buildComparableState();
        }
    }, [formData, sampleRows, cocData, selectedAnalyses, micCAddon, paymentInformation, sameAnalysesForAll]);

    const hasFormData = () => {
        if (pristineSnapshotRef.current === null) {
            return false;
        }
        return buildComparableState() !== pristineSnapshotRef.current;
    };

    function resetFormForLabSwitch(newLabId: number) {
        const defaultCustomerInfo = getDefaultCustomerInfo();

        setFormData({
            labId: newLabId,
            contactName: defaultCustomerInfo.contactName,
            company: defaultCustomerInfo.company,
            address: {
                street1: defaultCustomerInfo.address.street1,
                street2: defaultCustomerInfo.address.street2,
                city: defaultCustomerInfo.address.city,
                postalCode: defaultCustomerInfo.address.postalCode,
                provinceName: defaultCustomerInfo.address.provinceName,
                country: defaultCustomerInfo.address.country,
            },
            officePhone: defaultCustomerInfo.officePhone,
            contactEmail: defaultCustomerInfo.contactEmail,
            purchaseOrder: '',
            quoteNumber: '',
            costCenter: defaultCustomerInfo.costCenter,
            isRushOrder: false,
            specialHandling: '',
            holdTimeInstruction: '',
            collaborators: [''],
            collaboratorErrors: {},
        });
        setCocData({ selectedPresetId: null, selectedGroups: [], selectedAnalyses: [], selectedPanels: {} });
        setSelectedAnalyses([]);
        setMicCAddon(false);
        setPaymentInformation('CC');
        setSameAnalysesForAll(false);
        setSharedAnalysesError(false);
        setSharedOtherTextError(false);
        setSampleRows([{
            sampleNumber: 1,
            sampleName: '',
            sampleSite: '',
            sampleLocation: '',
            sampleType: '',
            amountCollected: '',
            units: '',
            preserved: true,
            collectionDate: fillNewDate(),
            siteOptions: [],
            locationOptions: [],
            siteSource: '',
            locationSource: '',
            siteId: '',
            locationId: '',
            analyses: [],
            customAnalysis: '',
            clientSampleId: '',
            foodSampleType: '',
            shelfLife: '',
            storageConditions: '',
            comments: '',
        }]);
        setNameError(''); setCompanyError(''); setStreetError(''); setCityError('');
        setProvinceError(''); setPostalError(''); setPhoneError(''); setPurchaseOrderError('');
        setAnalysesError(''); setSamplesError(false); setCollectionDateError(false); setAddMoreError(false);
        setValidCollaborators([]); setSubmissionData(null);
        pristineSnapshotRef.current = null;
    }  

    // Handler to add a new sample row
    const handleAddSampleRow = () => {
        if(sampleRows.length+1 >= 10 && !isFoodLab) {
            setAddMoreError(true);
            if(sampleRows.length >= 10)
                return;
        }else if(sampleRows.length+1 >= 20 && isFoodLab){
            setAddMoreError(true);
            if(sampleRows.length >= 20)
                return;
        }
        setSampleRows((prev) => [
            ...prev,
            {
                sampleNumber: prev.length +1,
                sampleName: '',
                sampleSite: '',
                sampleLocation: '',
                sampleType: '',
                amountCollected: '',
                units: '',
                preserved: true,
                collectionDate: fillNewDate(),
                siteOptions: [],
                locationOptions: [],
                siteSource: '',
                locationSource: '',
                siteId: '',
                locationId: '',
                analyses: sameAnalysesForAll ? (prev[0]?.analyses ?? []) : [],
                customAnalysis: sameAnalysesForAll ? (prev[0]?.customAnalysis ?? '') : '',
                clientSampleId: '',
                foodSampleType: '',
                shelfLife: '',
                storageConditions: '',
                comments: '',
            },
        ]);
    };

    // Handler to update a sample row field
    const handleSampleRowChange = <K extends keyof SampleRow>(index: number, field: K, value: SampleRow[K]) => {
        setSampleRows((prev) => {
            const updated = prev.map((row, i) => i === index ? { ...row } : row);
            // Auto-update units if sampleType changes
            if (field === 'sampleType') {
                updated[index].units = value ? sampleTypeToUnits[value as keyof typeof sampleTypeToUnits] as SampleRow['units'] : '';
            }
            // Reset location if site changes
            if (field === 'sampleSite') {
                updated[index].sampleLocation = '';
            }

            if (field === 'collectionDate' && updated[index].collectionDate !== null && new Date(updated[index].collectionDate).getTime() > new Date().getTime()) {
                setCollectionDateError(true);
            } else if (field === 'collectionDate') {
                setCollectionDateError(false);
            }

            updated[index][field] = value;
            return updated;
        });
    };

    // Handler to delete a sample row
    const handleDeleteSampleRow = (deletedIdx: number) => {
        setAddMoreError(false);
        setSampleRows((prev) => {
            if (prev.length === 1) return prev;
            const updatedRows = prev
                .filter((_, filterIdx) => filterIdx !== deletedIdx)
                .map((row, newIdx) => {
                    const isDuplicate = prev
                        .filter((_, prevIdx) => prevIdx !== deletedIdx)
                        .some((comparisonRow, comparisonIdx) => 
                            comparisonIdx !== newIdx && 
                            comparisonRow.sampleName === row.sampleName && 
                            row.sampleName !== ''
                        );
                    const updatedErrors = { ...(row.hasErrors || {}) };
                    if (isDuplicate) {
                        updatedErrors.sampleName = true;
                    } else {
                        delete updatedErrors.sampleName;
                    }
                    return {
                        ...row,
                        sampleNumber: newIdx + 1,
                        hasErrors: updatedErrors,
                    };
                });
            return updatedRows;
        });
    };

    async function checkErrorsAndOpenReview() {
        // Validate required fields & event logging
        if (checkFormErrors()) {
            return;
        }

        // Validate collaborator emails
        const newErrors: { [key: number]: string } = {};
        const validCollaborators = formData.collaborators.filter((email, index) => {
            if (!email) return false;
            if (!validateEmail(email)) {
                newErrors[index] = 'Invalid email format';
                return false;
            }
            return true;
        });

        if (Object.keys(newErrors).length > 0) {
            setFormData(prev => ({
                ...prev,
                collaboratorErrors: newErrors
            }));
            return;
        }

        let submissionAnalysisIds: number[] = [];
        if (!isFoodLab) {
            if (isChampionX) {
                const automaticAnalyses: number[] = [];

                for (const preset of presets) {
                    let response = await ApiService.getCocGroups(preset.id);
                    let groups: CocGroup[] = response.data;

                    for (const group of groups) {
                        response = await ApiService.getCocPanelAnalyses(group.id);
                        let panels: CocPanel[] = response.data;

                        for (const panel of panels) {
                            for (const analysis of panel.analyses) {
                                if (analysis.name !== "Corrosion Biomarker (micC)" || micCAddon) {
                                    automaticAnalyses.push(analysis.id);
                                }
                            }
                        }
                    }
                }

                submissionAnalysisIds = automaticAnalyses;
            } else {
                submissionAnalysisIds = cocData.selectedAnalyses;
            }
        }

        const buildReviewSample = (row: SampleRow) => {
            const analysisIds = isFoodLab ? row.analyses : submissionAnalysisIds;
            const customAnalysis = isFoodLab ? (row.customAnalysis || undefined) : undefined;

            const analysisVOs = isFoodLab
                ? analysisIds.map(id => {
                    const match = foodAnalyses.find(a => a.id === id);
                    return { id, name: match?.name ?? '', variant: match?.variant ?? '' };
                })
                : analysisIds.map(id => ({ id, name: '', variant: '' }));

            return {
                inventoryNumber: row.sampleNumber,
                name: isFoodLab ? undefined : (row.sampleName || undefined),
                type: isFoodLab ? undefined : row.sampleType,
                amount: isFoodLab ? '' : row.amountCollected,
                units: isFoodLab ? '' : row.units,
                preserved: true,
                collectionDate: new Date(row.collectionDate).getTime(),
                siteId: isFoodLab ? '' : String(row.siteId),
                locationId: isFoodLab ? '' : String(row.locationId),
                siteName: isFoodLab ? '' : row.sampleSite,
                locationName: isFoodLab ? '' : row.sampleLocation,
                newSite: isFoodLab ? false : row.siteSource === 'NEW',
                newLocation: isFoodLab ? false : row.locationSource === 'NEW',
                inventoryId: isFoodLab ? undefined : String(row.sampleNumber),
                clientSampleId: isFoodLab ? row.clientSampleId : undefined,
                comments: isFoodLab ? (row.comments || undefined) : undefined,
                customAnalysis,
                cocAnalysisVOs: analysisVOs,
            };
        };

        const buildApiSample = (row: SampleRow) => {
            const analysisIds = isFoodLab ? row.analyses : submissionAnalysisIds;
            const customAnalysis = isFoodLab ? (row.customAnalysis || undefined) : undefined;

            return {
                inventoryNumber: row.sampleNumber,
                name: isFoodLab ? undefined : (row.sampleName || undefined),
                type: isFoodLab ? 'food' : row.sampleType,
                amount: isFoodLab ? '' : row.amountCollected,
                units: isFoodLab ? '' : row.units,
                preserved: true,
                collectionDate: new Date(row.collectionDate).getTime(),
                siteId: isFoodLab ? '' : String(row.siteId),
                locationId: isFoodLab ? '' : String(row.locationId),
                siteName: isFoodLab ? '' : row.sampleSite,
                locationName: isFoodLab ? '' : row.sampleLocation,
                newSite: isFoodLab ? false : row.siteSource === 'NEW',
                newLocation: isFoodLab ? false : row.locationSource === 'NEW',
                clientSampleId: isFoodLab ? row.clientSampleId : undefined,
                comments: isFoodLab ? (row.comments || undefined) : undefined,
                customAnalysis,
                cocAnalysisVOs: analysisIds.map(id => ({ id, name: '', variant: '' })),
            };
        };

        const reviewSamples = sampleRows.map(buildReviewSample);
        const payloadSamples = sampleRows.map(buildApiSample);

        // Prepare form data for submission
        const submissionData = {
            labId: formData.labId !== null ? formData.labId : null,
            submissionDate: Date.now(),
            contactName: formData.contactName,
            company: formData.company || undefined,
            email: formData.contactEmail,
            purchaseOrderNumber: (paymentInformation === "PO" || isFoodLab) ? formData.purchaseOrder : undefined,
            quoteNumber: formData.quoteNumber || undefined,
            costCenterNumber: formData.costCenter === '' ? undefined : formData.costCenter,
            rushOrder: formData.isRushOrder ? 'Y' : 'N',
            specialRequests: formData.specialHandling || undefined,
            unpreservedInstructions: formData.holdTimeInstruction,
            collaboratorEmails: validCollaborators,
            cocSamples: payloadSamples,
            ...(isFoodLab ? {} : { cocAnalysesIds: submissionAnalysisIds }),
            addressInfo: {
                streetAddress1: formData.address.street1,
                streetAddress2: formData.address.street2 || undefined,
                city: formData.address.city,
                officePhone: formData.officePhone,
                countryName: formData.address.country,
                provinceName: formData.address.provinceName,
                postalCode: formData.address.postalCode
            },
        };

        setCocForm({
            labId: submissionData.labId,
            submissionDate: submissionData.submissionDate,
            contactName: submissionData.contactName,
            company: submissionData.company ?? "",
            email: submissionData.email,
            purchaseOrderNumber: submissionData.purchaseOrderNumber ?? "",
            quoteNumber: submissionData.quoteNumber ?? "",
            rushOrder: submissionData.rushOrder,
            specialRequests: submissionData.specialRequests ?? "",
            unpreservedInstructions: submissionData.unpreservedInstructions ?? "",
            cocId: "",
            collaboratorEmails: validCollaborators,
            cocSamples: reviewSamples,
            cocAnalysisVOs: selectedAnalyses,
            sameAnalysesForAll: sameAnalysesForAll,
            addressInfo: {
                streetAddress1: formData.address.street1,
                streetAddress2: formData.address.street2,
                city: formData.address.city,
                officePhone: formData.officePhone,
                countryName: formData.address.country,
                provinceName: formData.address.provinceName,
                postalCode: formData.address.postalCode,
                id: 0
            }
        });

        setSubmissionData(submissionData);
        setIsReviewMode(true);
        setIsDetailsOpen(true);
    }

    const handleSubmit = async () => {

        let apiErr = false;
       
        await ApiService.createCocLabRequest(submissionData).then((res: any) => {

            setCocId(res.data.cocId);
            setSubmissionDate(res.data.submissionDate);
            setContactName(res.data.contactName);
            setContactEmail(submissionData.email);
            setIsDetailsOpen(false);
            if (isFoodLab) {
                setCocForm(prev => ({
                    ...prev,
                    cocId: res.data.cocId,
                    submissionDate: res.data.submissionDate ?? prev.submissionDate,
                }));
            } else {
                setIsPackingSlipOpen(true);
            }
            addEventLog('CoC Form Submit Success', 'Lab Request', undefined, undefined, `CoC #: ${res.data.cocId}`);
            onSubmitSuccess?.(res.data, isFoodLab);
            onClose();
        }).catch((err) => {
            console.log('Error submitting form:', err);
            sendPopUpMessage('Error', 'An Error occured while submitting your form: '+ err.message, );
            addEventLog('CoC Form Submit Failed', 'Lab Request', undefined, undefined, `Error: ${err.message}`);
            apiErr = true;
        });

        if (!apiErr) {
            clearForm();
            if (isFoodLab) {
                setIsReviewMode(false);
                setIsDetailsOpen(true);
            } else {
                setIsDetailsOpen(false);
            }
        }
    };

    const validateEmail = (email: string): boolean => {
        return REGEX_CONSTANTS.EMAIL_REGEX.test(email);
    };

    const handleCollaboratorChange = (index: number, value: string) => {
        const newCollaborators = [...formData.collaborators];
        newCollaborators[index] = value;
        
        // Clear error when field is empty
        const newErrors = { ...formData.collaboratorErrors };
        if (!value) {
            delete newErrors[index];
        } else if (!validateEmail(value)) {
            newErrors[index] = 'Invalid email format';
        } else {
            delete newErrors[index];
        }
        
        setFormData(prev => ({
            ...prev,
            collaborators: newCollaborators,
            collaboratorErrors: newErrors
        }));
    };

    const handleAddCollaborator = () => {
        setFormData(prev => ({
            ...prev,
            collaborators: [...prev.collaborators, '']
        }));
    };

    const handleRemoveCollaborator = (index: number) => {
        setFormData(prev => {
            const newCollaborators = prev.collaborators.filter((_, i) => i !== index);
            const newErrors = { ...prev.collaboratorErrors };
            delete newErrors[index];
            
            // Adjust error indices after removal
            const adjustedErrors: { [key: number]: string } = {};
            Object.entries(newErrors).forEach(([key, value]) => {
                const numKey = parseInt(key);
                if (numKey > index) {
                    adjustedErrors[numKey - 1] = value;
                } else {
                    adjustedErrors[numKey] = value;
                }
            });
            
            return {
                ...prev,
                collaborators: newCollaborators,
                collaboratorErrors: adjustedErrors
            };
        });
    };
    const handleLabChange = (labId: number) => {
        const currentLabId = formData.labId;

        if (!foodLabEnabled && labId !== 1) return;

        if (currentLabId === null) {
            setFormData(prev => ({ ...prev, labId }));
            return;
        }

        if (hasFormData()) {
            setPendingLabId(labId);
            setIsSwitchLabDialogOpen(true);
        } else {
            resetFormForLabSwitch(labId);
        }
    };

    const confirmSwitchLab = () => {
        if (pendingLabId !== null) {
            resetFormForLabSwitch(pendingLabId);
        }
        setPendingLabId(null);
        setIsSwitchLabDialogOpen(false);
    };

    const cancelSwitchLab = () => {
        setPendingLabId(null);
        setIsSwitchLabDialogOpen(false);
    };
    
    const handleSameAnalysesForAllChange = (checked: boolean) => {
        setSameAnalysesForAll(checked);
        if (checked) {
            const sharedIds = sampleRows[0]?.analyses ?? [];
            const sharedOther = sampleRows[0]?.customAnalysis ?? '';
            setSampleRows(prev => prev.map(row => ({ ...row, analyses: sharedIds, customAnalysis: sharedOther })));
        }
    };

    const handleSharedAnalysesChange = (ids: number[]) => {
        setSampleRows(prev => prev.map(row => ({ ...row, analyses: ids })));
        setSharedAnalysesError(false);
    };

    const handleSharedOtherTextChange = (text: string) => {
        setSampleRows(prev => prev.map(row => ({ ...row, customAnalysis: text })));
        setSharedOtherTextError(false);
    };

    const HelpEmail = 
        <a
        target="_blank"
        rel="noopener noreferrer"
        href="mailto:help@luminultra.com"
        style={{ color: CONSTANTS.LINK_COLOR }}
        >
        help@luminultra.com
        </a>
    return (
        <ThemeProvider theme={theme}>
        <PackingSlip
            cocId = {cocId}
            submissionDate = {submissionDate}
            contactName = {contactName}
            contactEmail = {contactEmail}
            isOpen = {isPackingSlipOpen}
            onClose = {() => setIsPackingSlipOpen(false)}
            maxWidthOverride = {620}
        />

        <LabRequestDetails
            cocForm = {cocForm}
            isChampionX = {isChampionX}
            isOpen = {isDetailsOpen}
            onClose = {() => setIsDetailsOpen(false)}
            maxWidthOverride = {800}
            isReview = {isReviewMode}
            reviewFunction = {handleSubmit}
            isFoodLab = {isFoodLab}
        />

        <Dialog 
            open={isOpen} 
            onClose={(event, reason) => {
				if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
					onClose();
				}
			}}
            maxWidth={maxWidthOverride ? false : 'md'} 
            fullWidth
            sx={maxWidthOverride ? { '& .MuiDialog-paper': { maxWidth: maxWidthOverride + 'px' } } : {}}
        >
            <DialogContent>
                <Typography className="!font-inter !font-bold !text-[#2C2C2C] !text-[24px] !mb-4" gutterBottom>
                    {title ?? (isChampionX ? 'ChampionX CoC Form' : 'Standard CoC Form')}
                </Typography>

                {foodLabEnabled && (
                    <SelectableCardGroup value={formData.labId} onChange={handleLabChange} />
                )}

                {formData.labId === null ? (
                    <></>
                ) : (
                    <>
                    <Alert
                        icon={false} // hides the default info icon
                        sx={{
                            border: 1,
                            padding: 1,
                            paddingLeft:2,
                            mt: 0,
                            fontFamily: 'jost',
                            fontSize: '16px',
                            maxWidth: '800px',
                            color: '#2C2C2C',
                            '& ul': {
                            paddingLeft: '2rem',
                            margin: 0,
                            listStyleType: 'disc',
                            },
                            '& li': {
                            paddingLeft: '0.3rem',
                            marginBottom: '0.3rem',
                            },
                        }}
                        severity="info"
                    >
                        <Typography component="div" fontFamily="jost">
                            <p><strong>Please note:</strong></p>
                            <ul>
                            <li>Please ensure this form is filled out accurately and consistently to ensure ease of reporting.</li>
                            <li>Testing and turnaround time will commence once the physical samples are received.</li>
                            <li>
                                Submitted form indicates agreement with the privacy policy and terms of service.
                                For more information about our terms and conditions and turnaround times, visit{' '}
                                <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href="https://www.luminultra.com/coc/"
                                style={{ color: CONSTANTS.LINK_COLOR }}
                                >
                                luminultra.com/coc
                                </a>.
                            </li>
                            </ul>
                        </Typography>
                    </Alert>
                    
                    {/* Customer Information Section */}
                    <Box mb={3} maxWidth={800}>
                        <Typography style={{fontSize:20, fontFamily:"inter", marginTop:24, fontWeight:600, marginBottom:20}} gutterBottom>
                            Customer Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <ShrinkLabelTextField
                                    fullWidth
                                    label={isChampionX ? "ChampionX Contact Name" : "Contact Name"}
                                    value={formData.contactName}
                                    onChange={(e) => handleInputChange('contactName', e.target.value, setNameError)}
                                    required
                                    error={nameError !== ""}
                                    helperText={nameError}
                                    sx={{ marginBottom: 2 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {isChampionX ? 
                                    <TextField
                                        fullWidth
                                        label="ChampionX Customer Name"
                                        value={formData.company}
                                        onChange={(e) => handleInputChange('company', e.target.value)}
                                    />
                                :
                                    <TextField
                                        fullWidth
                                        label="Company"
                                        value={formData.company}
                                        onChange={(e) => handleInputChange('company', e.target.value, setCompanyError)}
                                        required
                                        error={companyError !== ""}
                                        helperText={companyError}
                                    />
                                }
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Street Address"
                                    value={formData.address.street1}
                                    onChange={(e) => handleAddressChange('street1', e.target.value, setStreetError)}
                                    required
                                    error={streetError !== ""}
                                    helperText={streetError}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <ShrinkLabelTextField
                                    fullWidth
                                    label="Street Address Line 2"
                                    value={formData.address.street2}
                                    onChange={(e) => handleAddressChange('street2', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="City"
                                    value={formData.address.city}
                                    onChange={(e) => handleAddressChange('city', e.target.value, setCityError)}
                                    required
                                    error={cityError !== ""}
                                    helperText={cityError}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="State/Province"
                                    value={formData.address.provinceName}
                                    onChange={(e) => handleAddressChange('provinceName', e.target.value, setProvinceError)}
                                    required
                                    error={provinceError !== ""}
                                    helperText={provinceError}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Postal / Zip Code"
                                    value={formData.address.postalCode}
                                    onChange={(e) => handleAddressChange('postalCode', e.target.value, setPostalError)}
                                    required
                                    error={postalError !== ""}
                                    helperText={postalError}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ "& .MuiFormControl-root": { width: "100%" } }}>
                                <DropdownField
                                    label="Country"
                                    value={formData.address.country}
                                    onChange={(value: string) => handleAddressChange('country', value)}
                                    options={countries}
                                    addStyle="!w-full country !font-jost"
                                />
                            </Grid>
                            {!isChampionX && (
                                <Grid item xs={12} sm={6}>
                                    <ShrinkLabelTextField
                                        fullWidth
                                        label="Contact Phone"
                                        value={formData.officePhone}
                                        onChange={(e) => handleInputChange('officePhone', e.target.value, setPhoneError)}
                                        required
                                        error={phoneError !== ""}
                                        helperText={phoneError}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Contact Email"
                                    value={formData.contactEmail}
                                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                                    required
                                    
                                />
                            </Grid>
                            {isChampionX && 
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Cost Center #"
                                        value={formData.costCenter}
                                        onChange={(e) => handleInputChange('costCenter', e.target.value)}
                                    />
                                </Grid>
                            }
                            {!isFoodLab && (
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Quote Number (if applicable)"
                                        value={formData.quoteNumber}
                                        onChange={(e) => handleInputChange('quoteNumber', e.target.value)}
                                        InputLabelProps={{
                                            sx: { fontSize: '0.95rem' }
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                    
                    {/* Payment Information Section */}
                    {(!isChampionX || isFoodLab) && (<Box 
                        mb={3} 
                        maxWidth={800} 
                    >
                        <Typography style={{fontSize:20, fontFamily:"inter", marginTop:24, fontWeight:600, marginBottom:20}} gutterBottom>
                            Payment Information
                        </Typography>
                        {isFoodLab ? (
                            <ShrinkLabelTextField
                                label="Purchase Order # (if applicable)"
                                value={formData.purchaseOrder}
                                onChange={(e) => handleInputChange("purchaseOrder", e.target.value)}
                                InputLabelProps={{
                                    sx: { fontSize: "0.95rem" },
                                }}
                                sx={{ minWidth: 280 }}
                            />
                        ) : (
                            <>
                            <Box
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                            display="flex"
                            gap={2}
                            >
                            <FormControl>
                                <FormLabel/>
                                <RadioGroup
                                name="controlled-radio-buttons-group"
                                value={paymentInformation}
                                onChange={(e) => setPaymentInformation(e.target.value)}
                                row
                                >
                                <FormControlLabel value="CC" control={<Radio />} label="Credit Card" />
                                <FormControlLabel value="PO" control={<Radio />} label="Purchase Order" />
                                </RadioGroup>
                            </FormControl>

                            <ShrinkLabelTextField
                            label= {`Purchase Order # ${paymentInformation === "PO" ? '*' : ''}`}
                            value={formData.purchaseOrder}
                            onChange={(e) => handleInputChange("purchaseOrder", e.target.value)}
                            disabled={paymentInformation !== "PO"}
                            InputLabelProps={{
                                sx: { fontSize: "0.95rem" },
                            }}
                            sx={{ minWidth: 280 }}
                            />
                            </Box>
                            {purchaseOrderError && formData.purchaseOrder.length === 0 &&(
                                <Typography variant="body2" color="red" paragraph>
                                    {purchaseOrderError}
                                </Typography>
                            )}
                            </>
                        )}
                    </Box>)
                    }

                    {/* Collaborators Section */}
                    <Box mb={3} maxWidth={800}>
                        <Typography style={{fontSize:20, fontFamily:"inter", marginTop:24, fontWeight:600, marginBottom:10}} gutterBottom>
                            Collaboration
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                        To protect confidentiality results will only be sent to the email address used to submit the form and the collaborators authorized below. 
                    
                        </Typography>
                        <Grid container spacing={2}>
                            {formData.collaborators.map((collaborator, index) => (
                                <Grid item xs={12} sm={12} key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <ShrinkLabelTextField
                                        fullWidth
                                        label={`Collaborator ${index + 1} email ${' '}`}
                                        value={collaborator}
                                        onChange={(e) => handleCollaboratorChange(index, e.target.value)}
                                        error={Boolean(formData.collaboratorErrors[index])}
                                        helperText={formData.collaboratorErrors[index]}
                                        sx={{ width:"50%", mr: 1 }}
                                    />
                                    {index > 0 && (
                                        <IconButton 
                                            onClick={() => handleRemoveCollaborator(index)} 
                                            color="error"
                                            size="small"
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                    )}
                                </Grid>
                            ))}
                            <Grid item xs={12}>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={handleAddCollaborator}
                                    sx={{
                                        textTransform: 'none',
                                        color: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 147, 200, 0.08)'
                                        }
                                    }}
                                >
                                    Add another
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>                
                    
                    {/* Report Access Section */}


                    {/* Sample Information Section */}
                        <Box mb={3}>
                        <Typography ref={sampleSectionRef} style={{fontSize:20, fontFamily:"inter", marginTop:24, fontWeight:600, marginBottom:10}} gutterBottom>
                                Sample Information
                            </Typography>
                            <Grid container spacing={2}>
                                {!isChampionX && !isFoodLab && (
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.isRushOrder}
                                                    onChange={(e) => handleInputChange('isRushOrder', e.target.checked)}
                                                />
                                            }
                                            label="Rush order? (50% surcharge will be applied to sample cost; rush orders not available for Sequencing analyses)"
                                        />
                                    </Grid>
                                )}

                                {/* Sample Information Table Section */}
                                <Grid item xs={12}>
                                    {samplesError && (
                                        <Typography variant="body2" color="red" paragraph>
                                            Please fill in the required sample fields.
                                        </Typography>
                                    )}
                                    {collectionDateError && (
                                        <Typography variant="body2" color="red" paragraph>
                                            Collection dates must not be in the future.
                                        </Typography>
                                    )}
                                    {isFoodLab && (
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: 'auto 1fr',
                                                columnGap: 1,
                                                rowGap: 0,
                                                mb: sameAnalysesForAll ? 0 : 1,
                                            }}
                                        >
                                            <Checkbox
                                                checked={sameAnalysesForAll}
                                                onChange={(e) => handleSameAnalysesForAllChange(e.target.checked)}
                                                size="small"
                                                sx={{ gridColumn: 1, gridRow: 1, p: 0 }}
                                            />
                                            <Typography sx={{ gridColumn: 2, gridRow: 1, alignSelf: 'center', lineHeight: 1.2 }}>
                                                Same analyses for all samples
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ gridColumn: 2, gridRow: 2, lineHeight: 1.2 }}
                                            >
                                                Turn this on when every sample needs the same tests. Turning it on replaces any analyses already chosen on individual rows.
                                            </Typography>
                                        </Box>
                                    )}
                                    {isFoodLab && sameAnalysesForAll && (
                                    <Box sx={{ mb: 1 }}>
                                        <Grid item xs={12}>
                                            <AnalysisSelect
                                                analyses={foodAnalyses}
                                                selectedIds={sampleRows[0]?.analyses ?? []}
                                                otherText={sampleRows[0]?.customAnalysis ?? ''}
                                                onChange={handleSharedAnalysesChange}
                                                onOtherTextChange={handleSharedOtherTextChange}
                                                error={sharedAnalysesError}
                                                errorMessage="Select at least one analysis."
                                                otherError={sharedOtherTextError}
                                            />
                                        </Grid>
                                    </Box>)
                                    }
                                    
                                    <SampleTable
                                        sampleRows={sampleRows}
                                        isChampionX={isChampionX}
                                        isFoodLab={isFoodLab}
                                        sameAnalysesForAll={sameAnalysesForAll}
                                        onRowChange={handleSampleRowChange}
                                        onAddRow={handleAddSampleRow}
                                        onDeleteRow={handleDeleteSampleRow}
                                        setSamplesErrors={setSamplesError}
                                        setAddMoreError={setAddMoreError}
                                        setCollectionDateError={setCollectionDateError}
                                        cocSites={cocSites}
                                        foodAnalyses={foodAnalyses}
                                        otherAnalysisId={foodOtherAnalysisId}
                                        addMoreError={addMoreError}
                                    />
                                </Grid>

                                {isChampionX && (
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={micCAddon}
                                                    onChange={(e) => setMicCAddon(e.target.checked)}
                                                />
                                            }
                                            label="Include micC qPCR analysis on all samples"
                                        />
                                    </Grid>
                                )}

                                {(!isChampionX || isFoodLab) && (
                                    <Grid item xs={12}>
                                        <ShrinkLabelTextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            sx={{ maxWidth: 800 }}
                                            label={isFoodLab ? "Additional Comments or Instructions" : "Special handling requests"}
                                            inputProps={{ maxLength: 255}}
                                            value={formData.specialHandling}
                                            onChange={(e) => handleInputChange('specialHandling', e.target.value)}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                    {/* Analyses Required Section */}
                    {!isChampionX && !isFoodLab && (
                        <Box ref={analysesSectionRef} mb={3}>
                            <Typography variant="body2" color="textSecondary" paragraph>
                                Analyses selected below will be performed on <u>all samples submitted</u>. If samples require different analyses, please submit separate forms. 
                                <br></br>
                                Select a preset to quickly choose analyses based on your needs. You can also select individual analyses below.
                            </Typography>

                            {analysesError !== "" && cocData.selectedAnalyses.length === 0 && (
                                <Typography variant="body2" color="red" paragraph>
                                    {analysesError}
                                </Typography>
                            )}

                            
                            {/* Preset Selection */}
                            <Box mb={2}>
                                <Typography component="span" fontFamily="jost" fontWeight='600'>PRESETS: </Typography>
                                <ToggleButtonGroup
                                    value={cocData.selectedPresetId}
                                    exclusive
                                    onChange={(_, value) => value && handlePresetChange(value)}
                                    aria-label="preset selection"
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            padding: '6px 12px',
                                            fontSize:16,
                                            marginLeft:1
                                        },
                                        '& .Mui-selected': {
                                            backgroundColor: theme => theme.palette.primaryLuBlue100.main + '!important',
                                            color: theme => theme.palette.primary.light + '!important',
                                        }
                                    }}
                                >
                                    {presets.map((preset) => (
                                        <ToggleButton
                                            key={preset.id}
                                            value={preset.id}
                                            aria-label={preset.name}
                                        >
                                            {preset.name}
                                        </ToggleButton>
                                        
                                    ))}
                                </ToggleButtonGroup>
                            </Box>

                            {/* Groups and Analyses */}
                            {cocData.selectedPresetId && (
                                <Box>
                                    <List component="nav">
                                        {groups.map((group) => {
                                            const isOpen = cocData.selectedGroups.includes(group.id);
                                            return (
                                                <React.Fragment key={group.id}>
                                                    <ListItem
                                                        button
                                                        onClick={() => handleGroupChange(group.id)}
                                                        sx={isOpen ? { 
                                                            bgcolor: 'primary.light',
                                                            color: 'primary.contrastText',
                                                            '&:hover': {
                                                                bgcolor: 'primary.main',
                                                            }
                                                        } : {}}
                                                    >
                                                        <ListItemText primary={group.name} sx={isOpen ? { color: 'primary.contrastText' } : {}} />
                                                        {isOpen ? <ExpandLess sx={isOpen ? { color: 'primary.contrastText' } : {}} /> : <ExpandMore />}
                                                    </ListItem>
                                                    <Collapse in={isOpen && !!panels[group.id]} timeout="auto" unmountOnExit>
                                                        {panels[group.id] && (
                                                            <Box pl={4} pt={1} pb={1}>
                                                                {panels[group.id].map((panel) => (
                                                                    <Box key={panel.id} mb={1}>
                                                                        {panel.name !== 'No Panel' ? (
                                                                            <>
                                                                            <Box display="flex" alignItems="center" mb={0.5} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1, pl: 1, pr: 1, py: 0.5 }}>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                checked={cocData.selectedPanels[panel.id]?.isSelected || false}
                                                                                indeterminate={cocData.selectedPanels[panel.id]?.isIndeterminate || false}
                                                                                onChange={(e) => handlePanelChange(group.id, panel.id, e.target.checked)}
                                                                                size="small"
                                                                                sx={isOpen ? {
                                                                                    color: 'primary.main',
                                                                                    '&.Mui-checked': {
                                                                                        color: 'primary.dark',
                                                                                    },
                                                                                } : {}}
                                                                            />
                                                                            <Typography variant="subtitle2" gutterBottom sx={{ mt: '5px', ...(isOpen ? { color: 'text.primary'} : {}) }}>
                                                                                {panel.name !== 'No Panel' ? panel.name : `Analyses`}
                                                                            </Typography>
                                                                        </Box>
                                                                        
                                                                        <List dense>
                                                                            {panel.analyses.map((analysis) => (
                                                                                <ListItem key={analysis.id} disablePadding sx={{ ml: 3 }}>
                                                                                    <ListItemIcon sx={{ minWidth: 'auto', marginRight: 1 }}>
                                                                                        <Checkbox
                                                                                            edge="start"
                                                                                            checked={cocData.selectedAnalyses.includes(analysis.id)}
                                                                                            onChange={(e) => handleAnalysisChange(group.id, panel.id, analysis.id, e.target.checked, analysis)}
                                                                                            size="small"
                                                                                            sx={isOpen ? {
                                                                                                color: 'primary.main',
                                                                                                '&.Mui-checked': {
                                                                                                    color: 'primary.dark',
                                                                                                },
                                                                                            } : {}}
                                                                                        />
                                                                                    </ListItemIcon>
                                                                                    <ListItemText
                                                                                        primary={analysis.name}
                                                                                        secondary={analysis.variant}
                                                                                        primaryTypographyProps={{ variant: 'body2', sx: isOpen ? { color: 'text.primary'} : {} }}
                                                                                        secondaryTypographyProps={{ variant: 'caption', sx: isOpen ? { color: 'text.secondary'} : {} }}
                                                                                    />
                                                                                </ListItem>
                                                                            ))}
                                                                        </List>
                                                                        </>
                                                                        ) :
                                                                        (
                                                                            <>
                                                                                {panel.analyses.map((analysis) => (
                                                                                    <Box key={analysis.id} display="flex" alignItems="center" mb={0.5} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1, pl: 1, pr: 1, py: 0.5 }}>
                                                                                        <Checkbox
                                                                                            edge="start"
                                                                                            checked={cocData.selectedAnalyses.includes(analysis.id)}
                                                                                            onChange={(e) => handleAnalysisChange(group.id, panel.id, analysis.id, e.target.checked, analysis)}
                                                                                            size="small"
                                                                                            sx={isOpen ? {
                                                                                                color: 'primary.main',
                                                                                                '&.Mui-checked': {
                                                                                                    color: 'primary.dark',
                                                                                                },
                                                                                            } : {}}
                                                                                        />
                                                                                        <Typography variant="subtitle2" gutterBottom sx={{ mt: '5px', ...(isOpen ? { color: 'text.primary' } : {}) }}>
                                                                                            {analysis.name}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                ))}
                                                                            </>
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Collapse>
                                                    <Box sx={{mt: 1}} />
                                                    <Divider sx ={{mt:2}} />
                                                </React.Fragment>
                                            );
                                        })}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    )}
                    {/* Shipping Information Section */}
                    <Box display="flex" alignItems="stretch" gap={1} width="800px">
                        <Alert
                            icon={false} // no default info icon
                            sx={{
                                border: 1,
                                padding: 2,
                                mt: 0,
                                fontFamily: 'jost',
                                fontSize: '16px',
                                maxWidth: '555px',
                                color: '#2C2C2C',
                            }}
                            severity="info"
                        >
                            <Typography component="div" fontFamily="jost">
                                <strong>Ship all samples to:</strong>
                                <br></br>
                                {isFoodLab ? (
                                    <p style={{margin:"10px 0"}}> LuminUltra Technologies Inc. <br></br>
                                                                    <em>Attn: Food Lab</em><br></br>
                                                                    819 Royal Road, Fredericton, NB E3G 6M1</p>
                                ) : (
                                    <p style={{margin:"10px 0"}}> LuminUltra Technologies Inc. <br></br>
                                                                    <em>Attn: Lab Services</em><br></br>
                                                                    805 Pinnacle Dr, Suite M, Linthicum Heights, MD, 21090</p>
                                )}
                                <p>{' '}
                                    {HelpEmail}</p>
                            </Typography>
                        </Alert>
                        <Alert
                            icon={false}
                            sx={{
                                border: 1,
                                padding: 2,
                                mt: 0,
                                fontFamily: 'jost',
                                fontSize: '16px',
                                maxWidth: '950px',
                                color: '#2C2C2C',
                                backgroundColor: '#e2e2e2',
                                '& ul': {
                                    paddingLeft: '1.5rem',
                                    margin: 0,
                                    listStyleType: 'none',
                                },
                                '& li': {
                                    marginBottom: '0.3rem',
                                    position: 'relative',
                                    paddingLeft: '1.2rem',
                                },
                                '& li::before': {
                                    content: '"\\2022"',
                                    position: 'absolute',
                                    left: '-0.3em',
                                    top: '-0.4em',
                                    fontSize: '52px',
                                    lineHeight: '1.4',
                                    color: '#2C2C2C',
                                },

                                
                            }}
                            severity="info"
                        >
                            <Typography component="div" fontFamily="jost" >
                                <Typography component="div" fontFamily="jost">
                                    <ul>
                                    <li>Need help with shipping or sample handling? <br></br>Review our Shipping Guidelines <a href='https://my.luminultra.com/hc/en-us/sections/44011654422797-Getting-Started-with-Lab-Services' target="_blank"
                                        style={{ color: CONSTANTS.LINK_COLOR }}>here</a>. </li>
                                    <li>Testing and turnaround time will commence once the physical samples are received.</li>
                                    <li>
                                        For shipping questions, contact Client Experience: 1-888-389-4491 or {HelpEmail} 
                                        &nbsp;(Monday to Friday 4 am - 4 pm EST).
                                    </li>
                                    <li>
                                        For testing guidance and support resources, visit the <a href='https://my.luminultra.com/hc/en-us' target="_blank"
                                        style={{ color: CONSTANTS.LINK_COLOR }}>myLuminultra Knowledge Center</a>.
                                    </li>
                                    <li>
                                        Additional shipping, customs, or special handling 
                                        fees may apply and will be invoiced as applicable.
                                    </li>
                                    </ul>
                                </Typography>
                            </Typography>
                        </Alert>
                    </Box>
                    <Divider sx={{ mt: 2 }} />
                    <Box sx={{mt: 2}}>
                        <Typography 
                            variant="body2"
                            color="textSecondary"
                            sx={{ gridColumn: 2, gridRow: 1, alignSelf: 'center', lineHeight: 1.2 }}>
                            All information submitted to the lab is confidential and protected according to regulatory requirement. When required by law to release any confidential information,
                            the customer will be notified in advance of the information to be released. Neither Luminultra, it's operating companies nor any of its employees shall be responsible for any claims,
                            loss or damages arising from consequences of default, error or reference to this submission, resulting report, or any omission in its preparation or the tests conducted.
                            Receipt of submission form and samples for processing will represent client agreement to the general terms and conditions of sale & service which can be found on 
                            {' '}
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href="https://www.luminultra.com"
                                style={{ color: CONSTANTS.LINK_COLOR }}
                                >
                                www.luminultra.com
                            </a>.
                        </Typography>
                    </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={openCacelFormDialog}>Cancel</Button>
                {formData.labId !== null && (
                    <PrimaryButton onClick={checkErrorsAndOpenReview} disabled={!navigator.onLine}>Review</PrimaryButton>
                )}
            </DialogActions>
        </Dialog>
        <CancelConfirmationDialog
            open={isCancelFormDialogOpen}
            onClose={closeCancelFormDialog}
            onConfirm={handleCancelForm}
        />
        <Dialog open={isSwitchLabDialogOpen} onClose={cancelSwitchLab}>
            <DialogTitle sx={{ letterSpacing: 'normal' }}>Switch labs?</DialogTitle>
            <DialogContent>
                <Typography>
                    Switching labs will clear this form. Any information you've entered will be lost. Continue?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={cancelSwitchLab}>No</Button>
                <Button onClick={confirmSwitchLab} variant="contained" color="primary">Yes</Button>
            </DialogActions>
        </Dialog>
        </ThemeProvider>
    );
};

export default LabRequestForm;